import { motion } from 'framer-motion'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { availabilityService } from '../services/availabilityService'
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CommandLineIcon,
  CheckIcon,
  SparklesIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

interface AvailabilitySetupModalProps {
  isOpen: boolean
  onClose: () => void
  onSetupComplete?: () => void
}

const AvailabilitySetupModal = ({ isOpen, onClose, onSetupComplete }: AvailabilitySetupModalProps) => {
  const { user } = useAuth()
  const [checking, setChecking] = useState(false)
  const [checkResult, setCheckResult] = useState<string | null>(null)
  const [showManualOverride, setShowManualOverride] = useState(false)
  const [diagnosticInfo, setDiagnosticInfo] = useState<string[]>([])

  const runDiagnostic = async () => {
    if (!user?.id) return
    
    const info: string[] = []
    setDiagnosticInfo([])
    setChecking(true)
    
    try {
      info.push('🔍 Starting diagnostic check...')
      setDiagnosticInfo([...info])
      
      // Check Supabase connection
      info.push('📡 Checking Supabase connection...')
      setDiagnosticInfo([...info])
      
      const tablesExist = await availabilityService.checkAvailabilityTablesExist()
      
      if (tablesExist) {
        info.push('✅ Tables detected successfully')
        info.push('🔧 Attempting to set up user profile...')
        setDiagnosticInfo([...info])
        
        const setupResult = await availabilityService.setupAvailabilitySystem(user.id)
        
        if (setupResult.success) {
          info.push('✅ Setup completed successfully!')
          setCheckResult('success')
          // Auto-close after showing success
          setTimeout(() => {
            onClose()
          }, 2000)
          onSetupComplete?.()
        } else {
          info.push(`❌ Setup failed: ${setupResult.message}`)
          setCheckResult(setupResult.message)
        }
      } else {
        info.push('❌ Tables not found')
        info.push('💡 Please run the SQL migration script')
        setCheckResult('Tables not found. Please run the SQL script.')
      }
      
      setDiagnosticInfo([...info])
    } catch (error) {
      info.push(`❌ Diagnostic failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      setDiagnosticInfo([...info])
      setCheckResult('Diagnostic failed. Check console for details.')
    } finally {
      setChecking(false)
    }
  }

  const handleManualOverride = async () => {
    if (!user?.id) return
    
    setChecking(true)
    setCheckResult(null)
    const info: string[] = []
    
    try {
      console.log('Manual override: forcing availability system setup...')
      info.push('🚀 Manual override: Forcing setup...')
      info.push('⚠️  Bypassing table existence checks')
      info.push('🔧 Creating user profile and preferences...')
      setDiagnosticInfo([...info])
      
      // Use the new force setup method
      const setupResult = await availabilityService.forceSetupAvailabilitySystem(user.id)
      console.log('Manual override result:', setupResult)
      
      if (setupResult.success) {
        info.push('✅ Force setup completed successfully!')
        setDiagnosticInfo([...info])
        setCheckResult('success')
        setTimeout(() => {
          onClose()
        }, 1500)
        onSetupComplete?.()
      } else {
        info.push(`❌ Force setup failed: ${setupResult.message}`)
        setDiagnosticInfo([...info])
        setCheckResult(`Manual override failed: ${setupResult.message}`)
      }
    } catch (error) {
      console.error('Manual override error:', error)
      info.push(`❌ Manual override failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      setDiagnosticInfo([...info])
      setCheckResult('Manual override failed. Please check the browser console for details.')
    } finally {
      setChecking(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-2xl glass-mockup rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Advanced Calendar Setup Required</h2>
              <p className="text-gray-400 text-sm">Database migration needed for premium features</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Current Status */}
            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-400 mt-0.5" />
                <div>
                  <h3 className="text-orange-400 font-medium mb-1">Database Setup Required</h3>
                  <p className="text-gray-300 text-sm">
                    The advanced calendar features require additional database tables that haven't been set up yet. 
                    This is a one-time setup process.
                  </p>
                </div>
              </div>
            </div>

            {/* What You'll Get */}
            <div className="space-y-4">
              <h3 className="text-white font-semibold flex items-center space-x-2">
                <SparklesIcon className="w-5 h-5 text-purple-400" />
                <span>Advanced Features You'll Unlock:</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Smart scheduling suggestions',
                  'Availability management',
                  'Buffer time settings',
                  'Client preference tracking',
                  'Travel time optimization',
                  'Workload balancing'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckIcon className="w-4 h-4 text-green-400" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Setup Instructions */}
            <div className="space-y-4">
              <h3 className="text-white font-semibold flex items-center space-x-2">
                <CommandLineIcon className="w-5 h-5 text-blue-400" />
                <span>Setup Instructions:</span>
              </h3>
              
              <div className="space-y-3">
                <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold">1</div>
                    <div>
                      <p className="text-white font-medium">Navigate to your Supabase Dashboard</p>
                      <p className="text-gray-400 text-sm">Go to your project's SQL Editor</p>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold">2</div>
                    <div>
                      <p className="text-white font-medium">Run the availability migration</p>
                      <p className="text-gray-400 text-sm">Copy and execute the SQL from: <code className="bg-black/30 px-1 rounded">docs/sql-migrations/availability_system_migration.sql</code></p>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold">3</div>
                    <div>
                      <p className="text-white font-medium">Refresh your application</p>
                      <p className="text-gray-400 text-sm">The advanced features will be automatically available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Migration File Path */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <div className="flex items-start space-x-3">
                <CommandLineIcon className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <h4 className="text-blue-400 font-medium mb-1">Migration File Location</h4>
                  <code className="text-gray-300 text-sm bg-black/30 px-2 py-1 rounded">
                    docs/sql-migrations/availability_system_migration.sql
                  </code>
                  <p className="text-gray-400 text-xs mt-2">
                    This migration creates all the necessary database tables for advanced calendar features
                    including availability profiles, time-off management, and smart suggestions.
                  </p>
                </div>
              </div>
            </div>

            {/* Temporary Alternative */}
            <div className="p-4 bg-gray-500/10 border border-gray-500/30 rounded-xl">
              <h4 className="text-gray-300 font-medium mb-2">Temporary Alternative</h4>
              <p className="text-gray-400 text-sm">
                Until you run the migration, the calendar will work with basic availability checking 
                using your existing shoots data. Smart suggestions and advanced features will not be available.
              </p>
            </div>

            {/* Check Result */}
            {checkResult && (
              <div className={`p-4 rounded-xl border ${
                checkResult === 'success' 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-red-500/10 border-red-500/30'
              }`}>
                <div className="flex items-center space-x-2">
                  {checkResult === 'success' ? (
                    <>
                      <CheckIcon className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-medium">Setup Complete!</span>
                    </>
                  ) : (
                    <>
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
                      <span className="text-red-400 font-medium">{checkResult}</span>
                    </>
                  )}
                </div>
                
                {/* Show force setup for common issues */}
                {checkResult !== 'success' && (checkResult.includes('duplicate') || checkResult.includes('already')) && (
                  <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-400 text-sm mb-2">
                      ✅ Good news! Your database tables exist and you already have some availability data.
                    </p>
                    <button
                      onClick={handleManualOverride}
                      disabled={checking}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 text-sm"
                    >
                      {checking ? 'Setting up...' : 'Complete Setup Now'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Diagnostic Information */}
            {diagnosticInfo.length > 0 && (
              <div className="p-4 bg-gray-500/10 border border-gray-500/30 rounded-xl">
                <h4 className="text-gray-300 font-medium mb-3 flex items-center space-x-2">
                  <CommandLineIcon className="w-4 h-4" />
                  <span>Diagnostic Information</span>
                </h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {diagnosticInfo.map((info, index) => (
                    <div key={index} className="text-sm text-gray-400 font-mono">
                      {info}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manual Override Option */}
            {checkResult && checkResult !== 'success' && (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-yellow-400 font-medium mb-2">Still Having Issues?</h4>
                    <p className="text-gray-300 text-sm mb-3">
                      If you're confident that you've run the SQL script successfully and the tables exist, 
                      you can try to force enable the availability features.
                    </p>
                    <button
                      onClick={() => setShowManualOverride(!showManualOverride)}
                      className="text-yellow-400 text-sm underline hover:text-yellow-300 transition-colors"
                    >
                      {showManualOverride ? 'Hide' : 'Show'} Manual Override Option
                    </button>
                    
                    {showManualOverride && (
                      <div className="mt-3 p-3 bg-black/20 rounded-lg">
                        <p className="text-gray-400 text-xs mb-3">
                          ⚠️ This will attempt to create your availability profile without checking if tables exist. 
                          Only use this if you're sure the SQL script ran successfully.
                        </p>
                        <button
                          onClick={handleManualOverride}
                          disabled={checking}
                          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black rounded-lg font-medium transition-colors disabled:opacity-50 text-sm"
                        >
                          Force Enable Features
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <div className="text-sm text-gray-400">
            Need help? Check the documentation or contact support
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Close
            </button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={runDiagnostic}
              disabled={checking}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {checking ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  <span>Checking...</span>
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-4 h-4" />
                  <span>Run Diagnostic</span>
                </>
              )}
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default AvailabilitySetupModal 